import React, { useState, useEffect, forwardRef, useMemo } from 'react'
import { normalizeActor } from '@/api/normalizers'
import { IndustriesIcon, FlagIcon } from '@maui/elements'
import { useDraft } from '@maui/hooks'
import { Details, Term, Definition } from '@/components/common/Details'
import Freemium from '@/components/common/Freemium/Freemium'
import { IconListGrid, NameWithSeenTooltipIcon } from '../../common/IconListGrid'
import { DetailsSection } from '../DetailsSection'

const TargetLocations = ({ locations, showTargetLocations, setShowTargetLocations }) => {
  if (locations === 'redacted') {
    return <Freemium layout='fullCenter' />
  }

  if (Array.isArray(locations) && locations.length > 0) {
    return (
      <IconListGrid
        list={locations}
        showItems={showTargetLocations}
        setShowItems={setShowTargetLocations}
        displayLimit={9}
        cellWidths={['100%', '50%', '33.33%']}
        renderItem={(item) => {
          const targetIconProps = {
            code: item.iso2,
            fallback: true,
          }
          return (
            <NameWithSeenTooltipIcon
              iconName=''
              iconComponent={FlagIcon}
              flagProps={targetIconProps}
              item={item}
              type={item.name}
            />
          )
        }}
      />
    )
  } else {
    return 'There are no known target regions at this time.'
  }
}

const TargetsDetails = forwardRef(({ actorData, expanded: { industriesList }, expandedUpdated }, ref) => {
  const [showIndustries, setShowIndustries] = useDraft(industriesList)
  const [showTargetLocations, setShowTargetLocations] = useState(false)

  const actor = useMemo(() => {
    if (!actorData) return null
    return normalizeActor(actorData)
  }, [actorData])

  useEffect(() => {
    if (industriesList) setShowIndustries(true)
  }, [expandedUpdated, industriesList, setShowIndustries])

  const { industries, targetLocations } = actor

  const Industries = ({ industries, showIndustries, setShowIndustries }) => {
    if (industries === 'redacted') {
      return <Freemium layout='fullCenter' />
    }

    if (Array.isArray(industries) && industries.length > 0) {
      return (
        <IconListGrid
          list={industries}
          showItems={showIndustries}
          setShowItems={setShowIndustries}
          type='industries'
          displayLimit={9}
          cellWidths={['100%', '50%', '33.33%']}
          renderItem={(item) => {
            return (
              <NameWithSeenTooltipIcon
                iconName={item.name}
                item={item}
                iconComponent={IndustriesIcon}
                type={item.name}
              />
            )
          }}
        />
      )
    } else {
      return 'There are no known target industries at this time.'
    }
  }

  if (industries?.length === 0 && targetLocations?.length === 0) {
    return (
      <DetailsSection ref={ref} title="Targets">
        <Details>
          There are no known target industries or regions at this time.
        </Details>
      </DetailsSection>
    )
  }

  return (
    <DetailsSection ref={ref} title="Targets">
      <Details>
        <Term marginTop='8px'>Industries</Term>
        <Definition marginTop='8px'>
          <Industries
            industries={industries}
            showIndustries={showIndustries}
            setShowIndustries={setShowIndustries}
          />
        </Definition>
        <Term>Regions</Term>
        <Definition>
          <TargetLocations
            locations={targetLocations}
            showTargetLocations={showTargetLocations}
            setShowTargetLocations={setShowTargetLocations}
          />
        </Definition>
      </Details>
    </DetailsSection>
  )
})

TargetsDetails.displayName = 'TargetsDetails'

export default TargetsDetails
